import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiList } from 'react-icons/fi';

const RANK_COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316','#EC4899','#14B8A6'];

const fmt  = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} MT`;

// Metric drives the bar length; "total" also stacks became_job vs the rest.
const BAR_METRICS = [
  { key: 'total',      label: 'Count',  fmt,        color: null      },
  { key: 'became_job', label: 'Job',    fmt,        color: '#10B981' },
  { key: 'weight',     label: 'Weight', fmt: fmtW,  color: '#6366F1' },
];

const SortableTh = ({ label, sortKey, currentSort, onSort }) => {
  const active = currentSort.key === sortKey;
  return (
    <th onClick={() => onSort(sortKey)}
      className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none tw-whitespace-nowrap">
      {label}{active && <span className="tw-ml-1 tw-text-gray-400">{currentSort.dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
};
SortableTh.propTypes = {
  label: PropTypes.string.isRequired, sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.object.isRequired, onSort: PropTypes.func.isRequired,
};

// Generic count/weight breakdown grouped by one dimension (region / sales unit
// / country / province), with Chart / Table views and a metric toggle. Rows
// drill via onItemClick(title, extraParams).
const QuotationBreakdownChart = ({
  title, Icon, iconColor, rowLabel, labelKey, subLabelKey, drillKey, drillTitlePrefix,
  data, loading, error, onItemClick, fullHeight,
  countryFilter, countryOptions, onCountryFilterChange,
}) => {
  const [metric,    setMetric]    = useState('total');
  const [view,      setView]      = useState('list');
  const [tableSort, setTableSort] = useState({ key: 'total', dir: 'desc' });
  const [tooltip,   setTooltip]   = useState({ visible: false, x: 0, y: 0, item: null, color: '' });

  if (loading) return (
    <Card padding="none" className={`tw-border tw-border-gray-200 tw-shadow-sm ${fullHeight ? 'tw-h-full' : ''}`}>
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card padding="none" className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm tw-mb-0">Error loading {title.toLowerCase()} data</p>
    </Card>
  );

  const activeMetric = BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0];
  const items  = [...(data || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const maxVal = Math.max(...items.map(r => r[metric] || 0), 1);

  const tableRows = [...(data || [])].sort((a, b) => {
    const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
    return tableSort.dir === 'desc' ? diff : -diff;
  });

  const totals = tableRows.reduce((s, r) => ({
    total:      s.total      + (r.total      || 0),
    became_job: s.became_job + (r.became_job || 0),
    lost:       s.lost       + (r.lost       || 0),
    weight:     s.weight     + (r.weight     || 0),
  }), { total: 0, became_job: 0, lost: 0, weight: 0 });

  const handleSort = (key) => {
    setTableSort(prev => prev.key === key
      ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' });
  };

  const handleClick = (row) => {
    if (!onItemClick) return;
    onItemClick(`${drillTitlePrefix}: ${row[labelKey]}`, { [drillKey]: row[labelKey] });
  };

  return (
    <Card padding="none" className={`tw-border tw-border-gray-200 tw-shadow-sm ${fullHeight ? 'tw-h-full' : ''}`}>
      {/* Hover tooltip (chart view) */}
      {tooltip.visible && tooltip.item && (
        <div style={{
          position: 'fixed', left: tooltip.x + 14, top: tooltip.y - 10, zIndex: 9999,
          pointerEvents: 'none', background: 'white', border: `2px solid ${tooltip.color}`,
          borderRadius: 10, padding: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 150,
        }}>
          <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{tooltip.item[labelKey] || 'Unknown'}</p>
          {subLabelKey && tooltip.item[subLabelKey] && (
            <p className="tw-text-xs tw-text-gray-400 tw-mb-0">{tooltip.item[subLabelKey]}</p>
          )}
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-mb-0">{fmt(tooltip.item.total)} quotations</p>
          <p className="tw-text-xs tw-text-emerald-600 tw-mb-0">Became Job: {fmt(tooltip.item.became_job)}</p>
          <p className="tw-text-xs tw-text-red-500 tw-mb-0">Lost: {fmt(tooltip.item.lost)}</p>
          {tooltip.item.weight > 0 && (
            <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold tw-mb-0">{fmtW(tooltip.item.weight)}</p>
          )}
        </div>
      )}

      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon size={15} className={iconColor} />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{title}</h3>
          <span className="tw-text-[10px] tw-text-gray-400">{items.length}</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          {onCountryFilterChange && (
            <select
              value={countryFilter || ''}
              onChange={(e) => onCountryFilterChange(e.target.value)}
              className="tw-text-xs tw-border tw-border-gray-200 tw-rounded tw-px-2 tw-py-1.5 tw-text-gray-700 tw-bg-white tw-cursor-pointer focus:tw-outline-none focus:tw-border-primary-400"
            >
              <option value="">All Countries</option>
              {(countryOptions || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
            <button type="button" onClick={() => setView('list')}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 ${view === 'list' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50'}`}>Chart</button>
            <button type="button" onClick={() => setView('table')}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50'}`}><FiList size={11} /> Table</button>
          </div>
          {view === 'list' && (
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              {BAR_METRICS.map((opt, idx) => (
                <button type="button" key={opt.key} onClick={() => setMetric(opt.key)}
                  className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50'}`}>{opt.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {view === 'list' ? (
        <div className="tw-p-4 tw-space-y-1.5 tw-max-h-80 tw-overflow-y-auto">
          {items.length === 0 ? (
            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-8 tw-mb-0">No data</p>
          ) : items.map((row, i) => {
            const pct   = maxVal > 0 ? ((row[metric] || 0) / maxVal) * 100 : 0;
            const color = RANK_COLORS[i % RANK_COLORS.length];
            const seg   = (v) => (row.total > 0 ? (v / row.total) * pct : 0);
            return (
              <div key={`${row[labelKey]}-${i}`} onClick={() => handleClick(row)}
                onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, item: row, color })}
                onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
                className="tw-group tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-rounded hover:tw-bg-primary-50/60 tw-px-2 tw-py-1.5 tw-transition-colors">
                <span className="tw-w-5 tw-h-5 tw-rounded-xl tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[10px] tw-font-bold" style={{ background: color }}>{i + 1}</span>
                <span className="tw-w-28 tw-flex-shrink-0 tw-text-xs tw-font-semibold tw-text-gray-700 tw-truncate">{row[labelKey] || 'Unknown'}</span>
                <div className="tw-flex-1 tw-h-4 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden tw-flex">
                  {metric === 'total' ? (
                    <>
                      <div className="tw-h-full" style={{ width: `${seg(row.became_job)}%`, background: '#10B981' }} />
                      <div className="tw-h-full" style={{ width: `${seg(Math.max(row.total - row.became_job - row.lost, 0))}%`, background: '#6366F1' }} />
                      <div className="tw-h-full" style={{ width: `${seg(row.lost)}%`, background: '#EF4444' }} />
                    </>
                  ) : (
                    <div className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500" style={{ width: `${pct}%`, background: activeMetric.color, opacity: 0.85 }} />
                  )}
                </div>
                <span className="tw-text-xs tw-font-bold tw-tabular-nums tw-w-20 tw-text-right" style={{ color: activeMetric.color || '#111827' }}>
                  {activeMetric.fmt(row[metric] || 0)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tw-overflow-auto tw-max-h-[420px]">
          <table className="tw-w-full tw-text-xs tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600 tw-min-w-28">{rowLabel}</th>
                <SortableTh label="Count"  sortKey="total"      currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Job"    sortKey="became_job" currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Lost"   sortKey="lost"       currentSort={tableSort} onSort={handleSort} />
                <SortableTh label="Weight" sortKey="weight"     currentSort={tableSort} onSort={handleSort} />
                <th className="tw-px-3 tw-py-2" />
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={`${row[labelKey]}-${i}`} onClick={() => handleClick(row)}
                  className={`tw-border-b tw-border-gray-100 tw-cursor-pointer ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-primary-50/60`}>
                  <td className="tw-px-3 tw-py-2">
                    <span className="tw-w-5 tw-h-5 tw-rounded-xl tw-inline-flex tw-items-center tw-justify-center tw-text-white tw-text-[10px] tw-font-bold" style={{ background: RANK_COLORS[i % RANK_COLORS.length] }}>{i + 1}</span>
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{row[labelKey] || 'Unknown'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-gray-900">{fmt(row.total)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-emerald-600">{fmt(row.became_job)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{row.lost ? fmt(row.lost) : '—'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{fmtW(row.weight)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-primary-400">↗</td>
                </tr>
              ))}
            </tbody>
            {tableRows.length > 0 && (
              <tfoot>
                <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                  <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={2}>Total ({tableRows.length})</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-900">{fmt(totals.total)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600">{fmt(totals.became_job)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-red-500">{fmt(totals.lost)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-primary-600">{fmtW(totals.weight)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Card>
  );
};

QuotationBreakdownChart.propTypes = {
  title: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  iconColor: PropTypes.string,
  rowLabel: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  subLabelKey: PropTypes.string,
  drillKey: PropTypes.string.isRequired,
  drillTitlePrefix: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onItemClick: PropTypes.func,
  fullHeight: PropTypes.bool,
  countryFilter: PropTypes.string,
  countryOptions: PropTypes.array,
  onCountryFilterChange: PropTypes.func,
};
QuotationBreakdownChart.defaultProps = {
  iconColor: 'tw-text-primary-500', data: [], loading: false, error: null,
  onItemClick: null, fullHeight: false, subLabelKey: null,
  countryFilter: '', countryOptions: [], onCountryFilterChange: null,
};

export default QuotationBreakdownChart;
